import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Checkbox, Empty, Input, InputNumber, Popconfirm, Select, Space, Spin, Switch, Tag, Typography, message } from 'antd';
import { ApiOutlined, CopyOutlined, DeleteOutlined, EditOutlined, KeyOutlined, PlusOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';
import { resolveFrontendApiBase } from '../utils/apiBase';
import { useAccountContext } from '../Account';
import './AccountIntegrations.css';
import AtticusConnections from '../atticus/AtticusConnections';
import { isSupportApprovalDocument, isSupportApprovalFramed } from '../utils/supportSecurity';

const API_BASE = resolveFrontendApiBase();
const { Title, Paragraph, Text } = Typography;

type Permission = 'read' | 'translate' | 'export';

interface IntegrationSettings {
  max_credits_per_job: number | null;
  enabled: boolean;
}

interface Connection {
  id: string;
  name: string;
  permissions: Permission[];
  expires_at?: string | null;
  last_used_at?: string | null;
  revoked_at?: string | null;
  key_version?: number | null;
}

interface ConnectionResponse {
  connection: Connection;
  api_key: string;
}

const formatDate = (value?: string | null): string => {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unavailable' : date.toLocaleString();
};

const readError = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  const sharedMessage = error?.response?.data?.error?.message;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (typeof sharedMessage === 'string' && sharedMessage.trim()) return sharedMessage;
  return fallback;
};

const defaultNewConnection = () => ({
  name: '',
  permissions: ['read'] as Permission[],
  expiresInDays: 90,
  translateConsent: false,
});

function operatorApiBase(): string {
  const configured = String(process.env.REACT_APP_OPERATOR_MCP_API_BASE_URL || '').trim();
  if (!configured) return '';
  try {
    const origin = new URL(configured);
    if (origin.protocol !== 'https:' || origin.username || origin.password || origin.search || origin.hash
        || origin.pathname !== '/') return '';
    const resolved = resolveFrontendApiBase(origin.origin).replace(/\/+$/, '');
    return resolved === origin.origin ? resolved : '';
  } catch { return ''; }
}

export default function AccountIntegrations({ operatorOnly = false }: { operatorOnly?: boolean }): JSX.Element {
  const apiBase = operatorOnly ? operatorApiBase() : API_BASE;
  const operatorDocumentReady = !operatorOnly || (isSupportApprovalDocument() && !isSupportApprovalFramed());
  const { isAuth, usersSharedState } = useAccountContext();
  const ownerId = typeof usersSharedState?.userID === 'string' ? usersSharedState.userID : '';
  const ownerVersion = useRef(0);
  const [settings, setSettings] = useState<IntegrationSettings>({ enabled: false, max_credits_per_job: null });
  const [creditCeilingDraft, setCreditCeilingDraft] = useState<number | null>(null);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [newConnection, setNewConnection] = useState(defaultNewConnection);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingConnection, setEditingConnection] = useState<{ name: string; permissions: Permission[]; translateConsent: boolean } | null>(null);
  const [savingConnection, setSavingConnection] = useState(false);
  const [busyConnectionId, setBusyConnectionId] = useState<string | null>(null);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);

  const endpointBase = useMemo(() => apiBase.replace(/\/$/, ''), [apiBase]);

  const requestHeaders = useCallback(async () => getAuthHeaders(), []);

  useEffect(() => {
    ownerVersion.current += 1;
    setNewApiKey(null);
    setConnections([]);
    setSettings({ enabled: false, max_credits_per_job: null });
    setCreditCeilingDraft(null);
    setEditingId(null);
    setEditingConnection(null);
    if (!isAuth || !ownerId) setLoading(false);
  }, [isAuth, ownerId, apiBase, operatorOnly]);

  const load = useCallback(async () => {
    if (!isAuth || !ownerId || !apiBase || !operatorDocumentReady) {
      setLoading(false);
      return;
    }
    const requestOwnerVersion = ownerVersion.current;
    setLoading(true);
    setLoadError(null);
    try {
      const headers = await requestHeaders();
      const [settingsResponse, connectionsResponse] = await Promise.all([
        axios.get<IntegrationSettings>(`${apiBase}/integrations/settings`, { headers }),
        axios.get<{ connections: Connection[] }>(`${apiBase}/integrations/connections`, { headers }),
      ]);
      if (requestOwnerVersion !== ownerVersion.current) return;
      setSettings({
        enabled: Boolean(settingsResponse.data?.enabled),
        max_credits_per_job: typeof settingsResponse.data?.max_credits_per_job === 'number'
          ? settingsResponse.data.max_credits_per_job
          : null,
      });
      setCreditCeilingDraft(typeof settingsResponse.data?.max_credits_per_job === 'number'
        ? settingsResponse.data.max_credits_per_job
        : null);
      setConnections(Array.isArray(connectionsResponse.data?.connections) ? connectionsResponse.data.connections : []);
    } catch (error) {
      if (requestOwnerVersion !== ownerVersion.current) return;
      setLoadError(readError(error, 'Integrations are unavailable right now. Please try again.'));
    } finally {
      if (requestOwnerVersion === ownerVersion.current) setLoading(false);
    }
  }, [isAuth, ownerId, requestHeaders, apiBase, operatorDocumentReady]);

  useEffect(() => { void load(); }, [load]);

  const saveSettings = async (next: IntegrationSettings) => {
    setSavingSettings(true);
    try {
      const headers = await requestHeaders();
      const response = await axios.patch<IntegrationSettings>(`${apiBase}/integrations/settings`, next, { headers });
      setSettings({
        enabled: Boolean(response.data?.enabled),
        max_credits_per_job: typeof response.data?.max_credits_per_job === 'number' ? response.data.max_credits_per_job : null,
      });
      setCreditCeilingDraft(typeof response.data?.max_credits_per_job === 'number' ? response.data.max_credits_per_job : null);
      message.success('Integration settings saved.');
    } catch (error) {
      message.error(readError(error, 'Could not save integration settings. Please try again.'));
    } finally {
      setSavingSettings(false);
    }
  };

  const setPermission = (permissions: Permission[], permission: Permission, checked: boolean): Permission[] => {
    if (permission === 'read') return permissions.includes('read') ? permissions : ['read', ...permissions];
    return checked ? Array.from(new Set([...permissions, permission])) as Permission[] : permissions.filter(item => item !== permission);
  };

  const createConnection = async () => {
    const name = newConnection.name.trim();
    if (!name) {
      message.error('Give this connection a name before creating its key.');
      return;
    }
    if (newConnection.permissions.includes('translate') && (!newConnection.translateConsent || !settings.max_credits_per_job)) {
      message.error('Set a credit ceiling and confirm Translate permission before creating this connection.');
      return;
    }
    const requestOwnerVersion = ownerVersion.current;
    setCreating(true);
    try {
      const headers = await requestHeaders();
      const response = await axios.post<ConnectionResponse>(`${apiBase}/integrations/connections`, {
        name,
        permissions: newConnection.permissions,
        expires_in_days: newConnection.expiresInDays,
      }, { headers });
      if (requestOwnerVersion !== ownerVersion.current) return;
      setConnections(current => [response.data.connection, ...current]);
      setNewApiKey(operatorOnly ? null : response.data.api_key);
      setNewConnection(defaultNewConnection());
      message.success(operatorOnly ? 'Connection created. Return to assistant sign-in to approve it.' : 'Connection created. Copy its key now.');
    } catch (error) {
      if (requestOwnerVersion !== ownerVersion.current) return;
      message.error(readError(error, 'Could not create this connection. Please try again.'));
    } finally {
      if (requestOwnerVersion === ownerVersion.current) setCreating(false);
    }
  };

  const copyApiKey = async () => {
    if (!newApiKey) return;
    try {
      await navigator.clipboard.writeText(newApiKey);
      message.success('API key copied.');
    } catch (_error) {
      message.error('Could not copy the API key. Select and copy it manually.');
    }
  };

  const startEditing = (connection: Connection) => {
    setEditingId(connection.id);
    setEditingConnection({
      name: connection.name,
      permissions: connection.permissions.includes('read') ? connection.permissions : ['read', ...connection.permissions],
      translateConsent: connection.permissions.includes('translate'),
    });
  };

  const saveConnection = async (connection: Connection) => {
    if (!editingConnection) return;
    const name = editingConnection.name.trim();
    if (!name) {
      message.error('Give this connection a name.');
      return;
    }
    if (editingConnection.permissions.includes('translate') && (!editingConnection.translateConsent || !settings.max_credits_per_job)) {
      message.error('Set a credit ceiling and confirm Translate permission before saving.');
      return;
    }
    setSavingConnection(true);
    try {
      const headers = await requestHeaders();
      const response = await axios.patch<Connection>(`${apiBase}/integrations/connections/${encodeURIComponent(connection.id)}`, {
        name,
        permissions: editingConnection.permissions,
      }, { headers });
      setConnections(current => current.map(item => item.id === connection.id ? response.data : item));
      setEditingId(null);
      setEditingConnection(null);
      message.success('Connection updated.');
    } catch (error) {
      message.error(readError(error, 'Could not update this connection. Please try again.'));
    } finally {
      setSavingConnection(false);
    }
  };

  const rotateConnection = async (connection: Connection) => {
    const requestOwnerVersion = ownerVersion.current;
    setBusyConnectionId(connection.id);
    try {
      const headers = await requestHeaders();
      const response = await axios.post<ConnectionResponse>(`${apiBase}/integrations/connections/${encodeURIComponent(connection.id)}/rotate`, {}, { headers });
      if (requestOwnerVersion !== ownerVersion.current) return;
      setConnections(current => current.map(item => item.id === connection.id ? response.data.connection : item));
      setNewApiKey(operatorOnly ? null : response.data.api_key);
      message.success(operatorOnly ? 'Authorization rotated. Reconnect your assistant.' : 'Key rotated. Copy the new key now.');
    } catch (error) {
      if (requestOwnerVersion !== ownerVersion.current) return;
      message.error(readError(error, 'Could not rotate this key. Please try again.'));
    } finally {
      if (requestOwnerVersion === ownerVersion.current) setBusyConnectionId(null);
    }
  };

  const revokeConnection = async (connection: Connection) => {
    setBusyConnectionId(connection.id);
    try {
      const headers = await requestHeaders();
      await axios.delete(`${apiBase}/integrations/connections/${encodeURIComponent(connection.id)}`, { headers });
      setConnections(current => current.map(item => item.id === connection.id ? { ...item, revoked_at: new Date().toISOString() } : item));
      message.success('Connection revoked.');
    } catch (error) {
      message.error(readError(error, 'Could not revoke this connection. Please try again.'));
    } finally {
      setBusyConnectionId(null);
    }
  };

  if (operatorOnly && !apiBase) return <Alert type="error" message="Operator assistant gateway is not configured." />;
  if (operatorOnly && isSupportApprovalFramed()) return <Alert type="error" message="Open assistant settings directly in your browser." />;
  if (operatorOnly && !isSupportApprovalDocument()) return <Alert type="info" message="Open protected assistant settings" action={<Button onClick={() => window.location.assign(window.location.href)}>Open secure page</Button>} />;
  if (loading) return <div className="account-integrations-loading"><Spin tip="Loading integrations…" /></div>;

  return (
    <section className="account-integrations" aria-labelledby="account-integrations-title">
      <div className="account-integrations-heading">
        <div>
          <Title level={2} id="account-integrations-title">Integrations</Title>
          <Paragraph>{operatorOnly ? "Configure the owned connection and spending limit for your assistant’s OAuth sign-in." : "Connect your own software or compatible MCP client to ScribeShadow using a scoped bearer key."}</Paragraph>
          {operatorOnly && <a href="/agent-platform/support-connect?manage=connections">Manage Support connections</a>}
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => void load()}>Refresh</Button>
      </div>

      {loadError ? <Alert type="error" showIcon message={loadError} action={<Button size="small" onClick={() => void load()}>Try again</Button>} /> : null}

      {!operatorOnly ? <AtticusConnections userId={ownerId} isAuth={isAuth} /> : null}

      <Alert
        className="account-integrations-notice"
        type="info"
        showIcon
        message={operatorOnly ? "Connect your assistant using OAuth" : "Keys are for your server or local client configuration"}
        description={operatorOnly ? "Choose this connection during assistant sign-in. No API key needs to be copied or pasted." : "Do not paste a key into a chat prompt or store it in a website. ScribeShadow shows each key only once and never stores it in this browser."}
      />

      {newApiKey ? (
        <Alert
          className="account-integrations-key-alert"
          type="success"
          showIcon
          message="Copy this API key now"
          description={<div className="account-integrations-key-row"><code aria-label="New API key">{newApiKey}</code><Button icon={<CopyOutlined />} onClick={() => void copyApiKey()}>Copy key</Button></div>}
          closable
          onClose={() => setNewApiKey(null)}
        />
      ) : null}

      <Card className="account-integrations-card" title={<><SafetyCertificateOutlined /> Access and spending</>}>
        <div className="account-integrations-setting-row">
          <div><Text strong>Allow external integrations</Text><Text type="secondary">Turn off to block all keys from making new requests.</Text></div>
          <Switch aria-label="Allow external integrations" checked={settings.enabled} loading={savingSettings} onChange={(enabled) => void saveSettings({ ...settings, enabled })} />
        </div>
        <label className="account-integrations-ceiling-label" htmlFor="integration-credit-ceiling">Maximum credits per external job</label>
        <Space.Compact className="account-integrations-ceiling-control">
          <InputNumber id="integration-credit-ceiling" min={1} precision={0} value={creditCeilingDraft ?? undefined} placeholder="No paid jobs allowed" onChange={(value) => setCreditCeilingDraft(typeof value === 'number' ? value : null)} />
          <Button loading={savingSettings} onClick={() => void saveSettings({ ...settings, max_credits_per_job: creditCeilingDraft })}>Save ceiling</Button>
        </Space.Compact>
        <Text type="secondary">This is a cap for each job, not a monthly budget. Repeated authorized jobs consume your normal ScribeShadow credit balance.</Text>
      </Card>

      <Card className="account-integrations-card" title={<><PlusOutlined /> Create a connection</>}>
        <div className="account-integrations-create-grid">
          <label>Name<Input value={newConnection.name} maxLength={80} placeholder="For example: my publishing script" onChange={(event) => setNewConnection(current => ({ ...current, name: event.target.value }))} /></label>
          <label>Key expires after<Select value={newConnection.expiresInDays} onChange={(expiresInDays) => setNewConnection(current => ({ ...current, expiresInDays }))} options={[30, 90, 180].map(value => ({ value, label: `${value} days` }))} /></label>
        </div>
        <PermissionControls permissions={newConnection.permissions} translateConsent={newConnection.translateConsent} onPermissionsChange={(permissions) => setNewConnection(current => ({ ...current, permissions, translateConsent: permissions.includes('translate') ? current.translateConsent : false }))} onConsentChange={(translateConsent) => setNewConnection(current => ({ ...current, translateConsent }))} setPermission={setPermission} />
        <Button type="primary" icon={<KeyOutlined />} loading={creating} disabled={!settings.enabled} onClick={() => void createConnection()}>{operatorOnly ? "Create connection" : "Create key"}</Button>
        {!settings.enabled ? <Text type="secondary">Enable external integrations before creating a key.</Text> : null}
      </Card>

      <Card className="account-integrations-card" title={<><ApiOutlined /> API and MCP endpoints</>}>
        <div className="account-integrations-endpoints">
          {!operatorOnly ? <div><Text strong>HTTP API</Text><code>{endpointBase}/v1</code></div> : null}
          <div><Text strong>MCP endpoint</Text><code>{endpointBase}/mcp</code></div>
        </div>
        <Text type="secondary">{operatorOnly ? "Use OAuth sign-in when adding this MCP endpoint to your assistant." : <>Use <code>Authorization: Bearer YOUR_KEY</code> from a client you control. Use a client that supports a configured bearer API key.</>}</Text>
      </Card>

      <div className="account-integrations-heading account-integrations-connections-heading"><Title level={3}>Your connections</Title></div>
      {connections.length === 0 ? <Empty description="No connections yet" /> : connections.map((connection) => {
        const revoked = Boolean(connection.revoked_at);
        const expired = Boolean(connection.expires_at && new Date(connection.expires_at).getTime() <= Date.now());
        const isEditing = editingId === connection.id && editingConnection;
        return <Card className="account-integrations-card account-integrations-connection" key={connection.id}>
          <div className="account-integrations-connection-header"><div><Title level={4}>{connection.name}</Title><Space wrap>{connection.permissions.map(permission => <Tag key={permission}>{permission}</Tag>)}{revoked ? <Tag color="error">Revoked</Tag> : expired ? <Tag color="warning">Expired</Tag> : <Tag color="success">Active</Tag>}</Space></div><Text type="secondary">Key version {connection.key_version ?? 1}</Text></div>
          <div className="account-integrations-metadata"><span>Expires: {formatDate(connection.expires_at)}</span><span>Last used: {formatDate(connection.last_used_at)}</span>{revoked ? <span>Revoked: {formatDate(connection.revoked_at)}</span> : null}</div>
          {isEditing ? <div className="account-integrations-edit"><label>Name<Input value={editingConnection.name} onChange={(event) => setEditingConnection(current => current ? { ...current, name: event.target.value } : current)} /></label><PermissionControls permissions={editingConnection.permissions} translateConsent={editingConnection.translateConsent} onPermissionsChange={(permissions) => setEditingConnection(current => current ? { ...current, permissions, translateConsent: permissions.includes('translate') ? current.translateConsent : false } : current)} onConsentChange={(translateConsent) => setEditingConnection(current => current ? { ...current, translateConsent } : current)} setPermission={setPermission} /><Space><Button type="primary" loading={savingConnection} onClick={() => void saveConnection(connection)}>Save changes</Button><Button onClick={() => { setEditingId(null); setEditingConnection(null); }}>Cancel</Button></Space></div> : <Space wrap className="account-integrations-actions"><Button icon={<EditOutlined />} disabled={revoked} onClick={() => startEditing(connection)}>Edit</Button><Button icon={<ReloadOutlined />} loading={busyConnectionId === connection.id} disabled={revoked} onClick={() => void rotateConnection(connection)}>Rotate key</Button><Popconfirm title="Revoke this connection?" description="It cannot make new requests after revocation." okText="Revoke" okButtonProps={{ danger: true }} onConfirm={() => void revokeConnection(connection)}><Button danger icon={<DeleteOutlined />} loading={busyConnectionId === connection.id} disabled={revoked}>Revoke</Button></Popconfirm></Space>}
        </Card>;
      })}

      <Alert type="warning" showIcon message="Revoking a key does not cancel accepted jobs" description={<span>Jobs already accepted keep their recorded authorization. Signed download links remain valid for at most five minutes. Check <Link to="/credit-history">credit and activity history</Link> for completed work.</span>} />
    </section>
  );
}

function PermissionControls({ permissions, translateConsent, onPermissionsChange, onConsentChange, setPermission }: { permissions: Permission[]; translateConsent: boolean; onPermissionsChange: (permissions: Permission[]) => void; onConsentChange: (value: boolean) => void; setPermission: (permissions: Permission[], permission: Permission, checked: boolean) => Permission[] }): JSX.Element {
  const translateSelected = permissions.includes('translate');
  return <div className="account-integrations-permissions" aria-label="Connection permissions"><Text strong>Permissions</Text><Checkbox checked disabled>Read</Checkbox><Checkbox checked={translateSelected} onChange={(event) => onPermissionsChange(setPermission(permissions, 'translate', event.target.checked))}>Translate</Checkbox><Checkbox checked={permissions.includes('export')} onChange={(event) => onPermissionsChange(setPermission(permissions, 'export', event.target.checked))}>Export</Checkbox>{permissions.includes('export') ? <Text type="secondary">Export allows this connected app to generate and download your owned deliverable files.</Text> : null}{translateSelected ? <Checkbox className="account-integrations-consent" checked={translateConsent} onChange={(event) => onConsentChange(event.target.checked)}>I authorize paid translation jobs up to the account credit ceiling.</Checkbox> : null}</div>;
}
